import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import { shortlistAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";
import { useAuth } from "context/AuthContext";
import { getUserRoleForComponents } from "utils/authHelpers";

const ClabBiodataShortlisted = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userRole = getUserRoleForComponents(user);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { getPhotoUrl, cachePhotoUrl, prefetchPhotoUrls, prefetchPhotoUrl } =
    useBiodataCache();
  const [shortlistList, setShortlistList] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "" });

  useEffect(() => {
    const loadShortlists = async () => {
      setLoading(true);
      try {
        const params = {
          page: pagination.currentPage,
          per_page: 10,
          search: filters.search || undefined,
          status: 0, // Only get active shortlists
        };
        const result = await shortlistAPI.getAll({ params });
        // Data is already filtered by API, but keep this as fallback
        const filtered = (result.data.data || []).filter((s) => s.status === 0);
        // Seed photo cache from shortlist payload to avoid extra fetches
        filtered.forEach((row) => {
          const b = row.biodata || {};
          const photo = b.photoUrl || b.photo_url || b.primary_photo_url;
          if (photo)
            cachePhotoUrl(b.id || b.biodata_id || row.biodata_id, photo);
        });
        prefetchPhotoUrls(
          filtered.map((row) => row.biodata?.id || row.biodata_id)
        );
        setShortlistList(filtered);
        setPagination({
          currentPage: result.data.pagination?.current_page || 1,
          totalPages: result.data.pagination?.total_pages || 1,
        });
      } catch (e) {
        setShortlistList([]);
      } finally {
        setLoading(false);
      }
    };
    loadShortlists();
  }, [filters, pagination.currentPage]);

  // Progressive photo prefetch when avatars enter viewport
  useEffect(() => {
    const elements = document.querySelectorAll("[data-biodata-id]");
    if (!elements || elements.length === 0) return;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = entry.target.getAttribute("data-biodata-id");
          const id = Number(idAttr) || idAttr;
          if (!id || seen.has(id)) return;
          const rec = shortlistList.find(
            (r) => (r.biodata?.id || r.biodata_id) === id
          );
          const b = rec?.biodata || {};
          const direct = b.photo_url || b.photoUrl || b.primary_photo_url;
          const cached = getPhotoUrl(id, b);
          if (!direct && !cached) prefetchPhotoUrl(id);
          seen.add(id);
          observer.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.25 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [shortlistList, getPhotoUrl, prefetchPhotoUrl]);

  // Pagination controls
  const handlePageChange = (page) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole={userRole}
        userName={userProfile?.full_name || "CLAB User"}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <RoleBasedSidebar
        userRole={userRole}
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6">
          <BreadcrumbNavigation userRole={userRole} />
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">
              Shortlisted Biodata
            </h1>
            <p className="text-text-secondary">
              View and track all biodata currently on hold by employers.
            </p>
          </div>
          {/* Filter controls */}
          <div className="mb-4 flex flex-wrap gap-4 items-center">
            <input
              type="text"
              placeholder="Search by name, passport, or worker ID..."
              className="border border-secondary-300 rounded px-3 py-2 text-sm"
              value={filters.search}
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
            />
          </div>
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="w-full">
              <thead className="bg-secondary-50 border-b border-secondary-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Worker No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Passport No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Nationality
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Skills
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Employer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Shortlist Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Time Left
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-secondary-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8">
                      Loading...
                    </td>
                  </tr>
                ) : shortlistList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-8 text-text-secondary"
                    >
                      No shortlisted biodata found.
                    </td>
                  </tr>
                ) : (
                  shortlistList.map((row) => {
                    // Use holdExpiryDate from API or calculate as fallback
                    const expiry = new Date(
                      row.holdExpiryDate ||
                        new Date(row.created_at).getTime() + 24 * 60 * 60 * 1000
                    );
                    const now = new Date();
                    const diffMs = expiry - now;
                    const hours = Math.max(0, Math.floor(diffMs / 3600000));
                    const mins = Math.max(
                      0,
                      Math.floor((diffMs % 3600000) / 60000)
                    );
                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-secondary-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 font-mono text-xs">
                          {row.biodata?.workerNo ||
                            row.biodata?.worker_no ||
                            row.biodata?.id ||
                            "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-10 h-10 rounded-full overflow-hidden bg-secondary-100"
                              data-biodata-id={
                                row.biodata?.id || row.biodata_id
                              }
                            >
                              <Image
                                src={getPhotoUrl(
                                  row.biodata?.id || row.biodata_id,
                                  row.biodata
                                )}
                                alt={
                                  row.biodata?.fullName ||
                                  row.biodata?.full_name
                                }
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <div className="font-medium text-text-primary">
                                {row.biodata?.fullName ||
                                  row.biodata?.full_name ||
                                  "-"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs">
                          {row.biodata?.passportNo ||
                            row.biodata?.passport_no ||
                            "-"}
                        </td>
                        <td className="px-6 py-4 text-xs">
                          {row.biodata?.nationality || "-"}
                        </td>
                        <td className="px-6 py-4 text-xs">
                          {Array.isArray(row.biodata?.skills)
                            ? row.biodata.skills.join(", ")
                            : row.biodata?.skills || row.biodata?.skill || "-"}
                        </td>
                        <td className="px-6 py-4 text-xs">
                          {row.employer?.companyName ||
                            row.employer?.company_name ||
                            "-"}
                        </td>
                        <td className="px-6 py-4 text-xs">
                          {row.created_at
                            ? new Date(row.created_at).toLocaleString()
                            : "-"}
                        </td>
                        <td
                          className="px-6 py-4 text-xs"
                          style={
                            diffMs < 3600000 && diffMs > 0
                              ? { color: "#d97706", fontWeight: "bold" }
                              : {}
                          }
                        >
                          {diffMs <= 0 ? "Expired" : `${hours}h ${mins}m`}
                        </td>
                        <td className="px-6 py-4">
                          <StatusIndicatorBadge status="on hold" size="sm" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          <div className="flex justify-center mt-6">
            {pagination.totalPages > 1 && (
              <nav className="inline-flex -space-x-px">
                <button
                  onClick={() =>
                    handlePageChange(Math.max(1, pagination.currentPage - 1))
                  }
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-1 border border-secondary-300 bg-surface text-text-secondary rounded-l hover:bg-secondary-50 disabled:opacity-50"
                >
                  Prev
                </button>
                {[...Array(pagination.totalPages)].map((_, idx) => (
                  <button
                    key={idx + 1}
                    onClick={() => handlePageChange(idx + 1)}
                    className={`px-3 py-1 border-t border-b border-secondary-300 bg-surface text-text-secondary hover:bg-secondary-50 ${
                      pagination.currentPage === idx + 1
                        ? "font-bold bg-primary-50 text-primary"
                        : ""
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    handlePageChange(
                      Math.min(
                        pagination.totalPages,
                        pagination.currentPage + 1
                      )
                    )
                  }
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-1 border border-secondary-300 bg-surface text-text-secondary rounded-r hover:bg-secondary-50 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClabBiodataShortlisted;
